import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import {
  Sparkles, Send, Loader2, MessageSquare, Table as TableIcon, Zap, HelpCircle,
  Download, BarChart2, Bookmark, BookmarkPlus, X, Trash2, Star, Play, Calendar, Mail, RefreshCw,
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip as ChartTooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const SAMPLE_QUESTIONS = [
  'Which properties are missing a PMS?',
  'Give me a portfolio summary',
  'What are the top 5 vendors by property coverage?',
  'Which properties have low coverage in Guest Services?',
  'Where is Opera deployed?',
];

const SKILL_LABELS = {
  properties_missing_platform_type: 'Missing platform',
  properties_with_vendor: 'Vendor deployments',
  top_platform_types_by_property_count: 'Top platform types',
  top_vendors_by_property_count: 'Top vendors',
  properties_by_category_coverage: 'Category coverage',
  vendor_search: 'Vendor search',
  product_search: 'Product search',
  portfolio_summary: 'Portfolio summary',
};

/**
 * AskYourStack — natural-language query bar for LodgiFlow.
 * Renders a search input, sample chips, and result panel with summary + table.
 * Follow-ups: Export CSV, quick chart when table is 2-col numeric,
 * save-as-smart-view + smart view chips for one-tap re-runs.
 */

// ── CSV export ────────────────────────────────────────────────────────
function tableToCsv(rows) {
  if (!rows || rows.length === 0) return '';
  const cols = Object.keys(rows[0]);
  const esc = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const lines = [cols.join(',')];
  rows.forEach(r => lines.push(cols.map(c => esc(r[c])).join(',')));
  return lines.join('\n');
}

function downloadCsv(rows, filename) {
  const csv = tableToCsv(rows);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Chart heuristic ───────────────────────────────────────────────────
// Return { labelCol, valueCol } if the table looks like a 2-column
// label→number chart, else null.
function detectChartShape(rows) {
  if (!rows || rows.length === 0) return null;
  const cols = Object.keys(rows[0]);
  if (cols.length < 2) return null;
  // Find first numeric column
  const numericCol = cols.find(c => rows.every(r => {
    const v = r[c];
    return typeof v === 'number' || (typeof v === 'string' && !isNaN(Number(v)) && v !== '');
  }));
  if (!numericCol) return null;
  const labelCol = cols.find(c => c !== numericCol) || cols[0];
  return { labelCol, valueCol: numericCol };
}

// ── Save-view dialog ──────────────────────────────────────────────────
function SaveViewDialog({ open, onOpenChange, question, skill, onSave }) {
  const [name, setName] = useState('');
  const [weeklyDigest, setWeeklyDigest] = useState(false);
  useEffect(() => { if (open) { setName(''); setWeeklyDigest(false); } }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="ask-stack-save-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookmarkPlus className="h-4 w-4 text-violet-600" /> Save as smart view
          </DialogTitle>
          <DialogDescription className="text-[11px]">
            Give this question a short name. Anyone in your tenant can re-run it in one tap.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[11px] text-muted-foreground font-medium">Question</label>
            <p className="text-xs mt-1 rounded-md border bg-muted/30 p-2 italic">{question}</p>
          </div>
          <div>
            <label className="text-[11px] text-muted-foreground font-medium">Name</label>
            <Input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Weekly PMS coverage check"
              className="mt-1 h-8 text-xs"
              autoFocus
              data-testid="ask-stack-save-name"
            />
          </div>
          <label className="flex items-center gap-2 text-xs cursor-pointer rounded-md border bg-muted/20 p-2.5 hover:bg-muted/30" data-testid="ask-stack-save-weekly-toggle">
            <input type="checkbox" checked={weeklyDigest} onChange={(e) => setWeeklyDigest(e.target.checked)} className="h-3.5 w-3.5" />
            <div className="flex-1">
              <div className="font-medium">Include in Weekly Digest</div>
              <div className="text-[10px] text-muted-foreground">Emailed to your team every Monday · via Slack/Teams webhook if configured</div>
            </div>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => onSave(name, skill, weeklyDigest)} disabled={!name.trim()} data-testid="ask-stack-save-confirm">Save view</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AskYourStack({ token, onJumpProperty }) {
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'chart'
  const [smartViews, setSmartViews] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadSmartViews = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND}/api/ask-stack/smart-views`, { headers });
      setSmartViews(r.data.items || []);
    } catch { /* ignore */ }
  }, [headers]);

  useEffect(() => { loadSmartViews(); }, [loadSmartViews]);

  const ask = async (q) => {
    const query = (q ?? question).trim();
    if (!query) return;
    setQuestion(query);
    setLoading(true);
    setExpanded(true);
    setViewMode('table');
    try {
      const res = await axios.post(`${BACKEND}/api/ask-stack/query`, { question: query }, { headers });
      setResult(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to run query');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const clear = () => {
    setQuestion('');
    setResult(null);
    setExpanded(false);
  };

  const saveView = async (name, skill, weeklyDigest = false) => {
    try {
      await axios.post(`${BACKEND}/api/ask-stack/smart-views`, { name, question, skill, weekly_digest: weeklyDigest }, { headers });
      toast.success(`Saved smart view: ${name}${weeklyDigest ? ' (in weekly digest)' : ''}`);
      setSaveOpen(false);
      loadSmartViews();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const toggleWeekly = async (view) => {
    try {
      await axios.patch(`${BACKEND}/api/ask-stack/smart-views/${view.id}`, { weekly_digest: !view.weekly_digest }, { headers });
      toast.success(view.weekly_digest ? 'Removed from weekly digest' : 'Added to weekly digest');
      loadSmartViews();
    } catch { toast.error('Update failed'); }
  };

  const [digestOpen, setDigestOpen] = useState(false);
  const [digestData, setDigestData] = useState(null);
  const [digestBusy, setDigestBusy] = useState(false);

  const openDigest = async (regenerate = false) => {
    setDigestOpen(true);
    setDigestBusy(true);
    try {
      const r = regenerate
        ? await axios.post(`${BACKEND}/api/ask-stack/digest/generate`, {}, { headers })
        : await axios.get(`${BACKEND}/api/ask-stack/digest/latest`, { headers });
      const d = regenerate ? r.data : r.data.digest;
      setDigestData(d);
      if (regenerate) toast.success('Digest regenerated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load digest');
    } finally {
      setDigestBusy(false);
    }
  };

  const deleteView = async (id, name) => {
    if (!window.confirm(`Delete smart view "${name}"?`)) return;
    try {
      await axios.delete(`${BACKEND}/api/ask-stack/smart-views/${id}`, { headers });
      toast.success('Smart view deleted');
      loadSmartViews();
    } catch { toast.error('Delete failed'); }
  };

  const doExport = () => {
    const rows = result?.result?.table || [];
    if (rows.length === 0) return;
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(rows, `askstack-${stamp}.csv`);
    toast.success(`Exported ${rows.length} rows`);
  };

  const table = result?.result?.table || [];
  const columns = table.length > 0 ? Object.keys(table[0]) : [];
  const skillLabel = result?.plan?.skill ? (SKILL_LABELS[result.plan.skill] || result.plan.skill) : null;
  const chartShape = useMemo(() => detectChartShape(table), [table]);
  const chartRows = useMemo(() => {
    if (!chartShape) return [];
    return table.slice(0, 20).map(r => ({
      label: String(r[chartShape.labelCol] ?? ''),
      value: Number(r[chartShape.valueCol]) || 0,
    }));
  }, [chartShape, table]);

  return (
    <div className="rounded-xl border bg-gradient-to-br from-violet-50 via-fuchsia-50/30 to-white dark:from-violet-950/20 dark:via-fuchsia-950/10 dark:to-transparent overflow-hidden" data-testid="ask-your-stack">
      <div className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <div className="h-7 w-7 rounded-lg bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center shadow-sm">
            <Sparkles className="h-4 w-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Ask Your Stack</h3>
            <p className="text-[11px] text-muted-foreground">Type a question in plain English — LodgiFlow&apos;s AI planner will query your live data.</p>
          </div>
          <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1 bg-white/70" onClick={() => openDigest(false)} title="Weekly digest" data-testid="ask-stack-digest-btn">
            <Calendar className="h-2.5 w-2.5" /> Weekly digest
          </Button>
          <Badge variant="outline" className="text-[9px] gap-1 bg-white/60"><Zap className="h-2.5 w-2.5" /> AI</Badge>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="flex gap-2">
          <div className="relative flex-1">
            <MessageSquare className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="e.g. Which properties are missing a PMS?"
              className="pl-9 h-10 bg-white/80 border-violet-200 focus-visible:ring-violet-400"
              data-testid="ask-stack-input"
              disabled={loading}
            />
          </div>
          <Button type="submit" size="sm" className="h-10 gap-1.5 bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 shadow-sm" disabled={loading || !question.trim()} data-testid="ask-stack-submit">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            {loading ? 'Thinking…' : 'Ask'}
          </Button>
          {expanded && !loading && (
            <Button type="button" variant="ghost" size="sm" className="h-10 text-muted-foreground" onClick={clear} data-testid="ask-stack-clear">
              Clear
            </Button>
          )}
        </form>

        {/* Saved smart views chips */}
        {smartViews.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-0.5">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold self-center mr-1"><Star className="h-2.5 w-2.5 inline mr-1 mb-0.5 text-amber-500 fill-amber-400" />Saved views:</span>
            {smartViews.map(v => (
              <div key={v.id} className={`group inline-flex items-center gap-0.5 rounded-full border transition-colors ${v.weekly_digest ? 'bg-violet-100/80 border-violet-300' : 'bg-white/80 hover:border-violet-300'}`} data-testid={`ask-stack-view-${v.id}`}>
                <button
                  onClick={() => ask(v.question)}
                  className="text-[11px] pl-2.5 pr-1 py-1 hover:text-violet-700 flex items-center gap-1"
                  title={v.question}
                >
                  <Play className="h-2.5 w-2.5" /> {v.name}
                  {v.weekly_digest && <Calendar className="h-2.5 w-2.5 text-violet-600" />}
                </button>
                <button
                  onClick={() => toggleWeekly(v)}
                  className="opacity-0 group-hover:opacity-100 px-1 text-muted-foreground hover:text-violet-700 transition-opacity"
                  title={v.weekly_digest ? 'Remove from Monday digest' : 'Add to Monday digest'}
                  data-testid={`ask-stack-view-weekly-${v.id}`}
                >
                  <Calendar className="h-2.5 w-2.5" />
                </button>
                <button
                  onClick={() => deleteView(v.id, v.name)}
                  className="opacity-0 group-hover:opacity-100 pr-1.5 text-muted-foreground hover:text-destructive transition-opacity"
                  title="Delete view"
                  data-testid={`ask-stack-view-delete-${v.id}`}
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        {!expanded && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold self-center mr-1"><HelpCircle className="h-2.5 w-2.5 inline mr-1 mb-0.5" />Try:</span>
            {SAMPLE_QUESTIONS.map((q, i) => (
              <button
                key={i}
                onClick={() => ask(q)}
                className="text-[11px] px-2.5 py-1 rounded-full border bg-white/70 hover:bg-violet-100 hover:border-violet-300 transition-colors text-foreground"
                data-testid={`ask-stack-sample-${i}`}
              >
                {q}
              </button>
            ))}
          </div>
        )}
      </div>

      {expanded && (
        <div className="border-t bg-white/60 dark:bg-black/20 p-4" data-testid="ask-stack-result">
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> AI is picking the right query and running it against your data…
            </div>
          ) : result ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                {skillLabel && <Badge variant="outline" className="text-[10px] bg-violet-50 border-violet-200 text-violet-700">{skillLabel}</Badge>}
                <span className="text-[10px] text-muted-foreground">{result.ms} ms</span>
                <div className="flex-1" />
                {/* Follow-up actions */}
                {table.length > 0 && (
                  <>
                    {chartShape && (
                      <div className="flex bg-muted/30 rounded-md p-0.5 border">
                        <button
                          className={`text-[10px] px-2 py-1 rounded flex items-center gap-1 ${viewMode === 'table' ? 'bg-background shadow-sm' : 'text-muted-foreground'}`}
                          onClick={() => setViewMode('table')}
                          data-testid="ask-stack-view-table"
                        ><TableIcon className="h-2.5 w-2.5" /> Table</button>
                        <button
                          className={`text-[10px] px-2 py-1 rounded flex items-center gap-1 ${viewMode === 'chart' ? 'bg-background shadow-sm' : 'text-muted-foreground'}`}
                          onClick={() => setViewMode('chart')}
                          data-testid="ask-stack-view-chart"
                        ><BarChart2 className="h-2.5 w-2.5" /> Chart</button>
                      </div>
                    )}
                    <Button variant="outline" size="sm" className="h-6 text-[10px] gap-1" onClick={doExport} data-testid="ask-stack-export">
                      <Download className="h-2.5 w-2.5" /> CSV
                    </Button>
                  </>
                )}
                <Button variant="outline" size="sm" className="h-6 text-[10px] gap-1" onClick={() => setSaveOpen(true)} data-testid="ask-stack-save">
                  <Bookmark className="h-2.5 w-2.5" /> Save view
                </Button>
              </div>
              {result.plan?.explanation && (
                <p className="text-xs text-muted-foreground italic">{result.plan.explanation}</p>
              )}
              {result.result?.summary && (
                <p className="text-sm font-medium" data-testid="ask-stack-summary">{result.result.summary}</p>
              )}
              {result.result?.resolved_platform_type && (
                <p className="text-[10px] text-muted-foreground">Resolved to: <code>{result.result.resolved_platform_type}</code></p>
              )}
              {table.length > 0 && viewMode === 'chart' && chartShape && (
                <div className="border rounded-lg bg-card p-3" data-testid="ask-stack-chart">
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-2">
                    {chartShape.labelCol} → {chartShape.valueCol}
                  </div>
                  <ResponsiveContainer width="100%" height={Math.max(180, Math.min(400, chartRows.length * 30 + 40))}>
                    <BarChart data={chartRows} layout="vertical" margin={{ top: 4, right: 20, bottom: 4, left: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} opacity={0.3} />
                      <XAxis type="number" tick={{ fontSize: 10 }} />
                      <YAxis type="category" dataKey="label" width={120} tick={{ fontSize: 10 }} />
                      <ChartTooltip cursor={{ fill: 'rgba(139, 92, 246, 0.1)' }} />
                      <Bar dataKey="value" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
              {table.length > 0 && viewMode === 'table' && (
                <div className="border rounded-lg overflow-hidden bg-card">
                  <div className="px-3 py-1.5 border-b bg-muted/30 flex items-center gap-2">
                    <TableIcon className="h-3 w-3 text-muted-foreground" />
                    <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">Results ({table.length})</span>
                  </div>
                  <div className="max-h-[320px] overflow-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-muted/20 sticky top-0">
                        <tr>
                          {columns.map(c => (
                            <th key={c} className="px-3 py-2 text-left font-semibold text-[10px] uppercase tracking-wider text-muted-foreground">{c}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {table.slice(0, 100).map((row, i) => (
                          <tr key={i} className="border-t hover:bg-muted/10">
                            {columns.map(c => {
                              const v = row[c];
                              const isPropId = c === 'Property ID' && v && onJumpProperty;
                              return (
                                <td key={c} className="px-3 py-1.5">
                                  {isPropId ? (
                                    <button onClick={() => onJumpProperty(v)} className="text-primary hover:underline font-mono text-[10px]" title="Open property">{String(v).slice(0, 8)}…</button>
                                  ) : (
                                    <span>{v === null || v === undefined ? '—' : String(v)}</span>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {table.length > 100 && (
                    <div className="px-3 py-1.5 border-t text-[10px] text-muted-foreground bg-muted/10">Showing first 100 of {table.length} results.</div>
                  )}
                </div>
              )}
              {!result.plan?.skill && !result.result && (
                <div className="text-xs text-muted-foreground">
                  Try rephrasing with a specific keyword like <em>PMS</em>, <em>vendor</em>, <em>coverage</em>, or <em>summary</em>.
                </div>
              )}
            </div>
          ) : (
            <div className="text-xs text-muted-foreground">No result yet.</div>
          )}
        </div>
      )}

      <SaveViewDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        question={question}
        skill={result?.plan?.skill}
        onSave={saveView}
      />

      <Dialog open={digestOpen} onOpenChange={setDigestOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="ask-stack-digest-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-violet-600" /> Weekly Smart-View Digest
            </DialogTitle>
            <DialogDescription className="text-[11px]">
              Delivered every Monday 09:00 UTC. Regenerate anytime — Slack/Teams webhook fires if configured.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 pb-2 border-b">
            <Button size="sm" variant="outline" className="h-7 text-[10px] gap-1" onClick={() => openDigest(true)} disabled={digestBusy} data-testid="ask-stack-digest-regenerate">
              {digestBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              Generate now
            </Button>
            {digestData && (
              <span className="text-[10px] text-muted-foreground">
                {digestData.entries?.length || 0} view{digestData.entries?.length === 1 ? '' : 's'} · Week of {digestData.week_of}
              </span>
            )}
          </div>
          <div className="flex-1 overflow-auto">
            {digestBusy ? (
              <div className="p-8 flex items-center justify-center text-muted-foreground text-xs gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Running your saved views…
              </div>
            ) : digestData?.html ? (
              <div dangerouslySetInnerHTML={{ __html: digestData.html }} data-testid="ask-stack-digest-html" />
            ) : (
              <div className="p-8 text-center text-xs text-muted-foreground">
                No digest yet. Toggle a saved view to include it in the digest, then click <b>Generate now</b>.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
